import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import {
  isCommunityAdmin,
  isCommunityModerator,
} from "@circle-react/helpers/communityMemberHelpers";
import { usePlanHasCustomizeCommunity } from "@circle-react/hooks/useIsCustomizeCommunity";
import { useLinkToRoot } from "@circle-react/hooks/useLinkToRoot";
import { useSmOrMdScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { APPEARANCE, useThemeContext } from "@circle-react/providers/Theme";

import { getCommunityLogoUrlAndType } from "@/react/helpers/communityHelpers";
import { useCircleV4 } from "@/react/hooks/useCircleV4";

import { LogoTopSectionView } from "./LogoTopSectionView";
import { TopSectionContainer } from "./TopSectionContainer";
import { LogoTopSectionV4View } from "./v4/LogoTopSectionV4View";

interface LogoTopSectionProps {
  isPreviewMode?: boolean;
  previewCommunityName?: string;
  previewLogoType?: "logo" | "icon";
  previewLogoUrl?: string | null;
}

export const LogoTopSection = ({
  isPreviewMode = false,
  previewCommunityName = "",
  previewLogoType = "icon",
  previewLogoUrl = "",
}: LogoTopSectionProps) => {
  const { isCircleV4 } = useCircleV4();
  const { currentCommunity, currentCommunityMember } = usePunditUserContext();
  const { goToCommunityRootPath } = useLinkToRoot();
  const { currentAppearance, isDarkAppearance } = useThemeContext();
  const isCustomizeCommunityAvailable = usePlanHasCustomizeCommunity();
  const isAdmin = isCommunityAdmin(currentCommunityMember);
  const isModerator = isCommunityModerator(currentCommunityMember);

  const { url, type } = getCommunityLogoUrlAndType(
    currentCommunity,
    isDarkAppearance,
  );

  const isSmOrMdScreen = useSmOrMdScreenMediaQuery();
  const isCustomizeThemeAvailable = usePlanHasCustomizeCommunity();

  const {
    member_count: memberCount,
    moderation_count: moderationCount,
    dark_icon_url: darkIconUrl,
    dark_logo_url: darkLogoUrl,
  } = currentCommunity ?? {};

  let logoUrl = url;

  if (currentAppearance === APPEARANCE.DARK && isCustomizeThemeAvailable) {
    if (type === "logo" && darkLogoUrl) {
      logoUrl = darkLogoUrl;
    } else if (type === "icon" && darkIconUrl) {
      logoUrl = darkIconUrl;
    }
  }

  const viewProps = {
    communityName: isPreviewMode
      ? previewCommunityName
      : currentCommunity?.name,
    isAdmin,
    isModerator,
    logoType: isPreviewMode ? previewLogoType : type,
    logoUrl: isPreviewMode ? previewLogoUrl : logoUrl,
    showToggleTheme: true as const,
    showCustomizeTheme: isCustomizeCommunityAvailable && !isSmOrMdScreen,
    onLogoClick: goToCommunityRootPath,
    memberCount,
    moderationCount,
  };

  return (
    <TopSectionContainer hasPadding={false}>
      {isCircleV4 ? (
        <LogoTopSectionV4View {...viewProps} />
      ) : (
        <LogoTopSectionView {...viewProps} />
      )}
    </TopSectionContainer>
  );
};
